import { Link } from '@tanstack/react-router';
import { Age, Empty, RowSkeleton, TaskKey } from '@/components/common/bits';
import { BriefIcon, TaskIcon } from '@/components/common/StatusIcon';
import { Badge } from '@/components/ui/badge';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import type { AccountData } from '@/features/account/data';
import { PausedChip } from '@/features/actions/PauseActions';
import { labelForKind } from '@/lib/event-kinds';
import { heldNote, holdNeedsAPerson } from '@/lib/fleet';
import { minutesSince } from '@/lib/format';
import { stepsForGlance } from '@/lib/steps-glance';

/**
 * Your work, two lists side by side rather than two panels stacked. Neither is
 * the page's subject (both have a destination of their own) so they share one
 * region and one row of space.
 *
 * The steps column used to print every task the API returned, oldest fetch
 * order and all, two lines each - the second line was usually the fleet's own
 * hold reason ("all 1 registered workers are offline"), the same sentence
 * repeated on every queued row. Thirty of those is not a glance, it beats what
 * this page is trying to answer, and the two or three rows that actually
 * needed a decision were buried wherever that order put them. So this list is
 * ranked and capped instead of dumped: `stepsForGlance` (`lib/steps-glance.ts`)
 * puts a step waiting on a review first, a paused step second, the one hold a
 * person has to end third, then everything the fleet is already moving on its
 * own - and cuts the result to a size a glance can hold. "Every step →" and the
 * heading's own count carry the rest.
 *
 * Each row is one line now: icon, key, title, then only the badge a person
 * would act on (Paused, or Held for the hold that does not clear itself). The
 * repeated fleet-state sentence is gone from here entirely - it is fleet
 * state, not step state, and the step's own page still says it once, where it
 * belongs.
 *
 * The heading reads "Steps at a glance" rather than "Your steps": once the
 * list is a ranked handful rather than literally every step, "Your steps"
 * reads as a claim of completeness the row count no longer backs. "At a
 * glance" is what this region already is, in the composition comment on
 * `me.tsx` and in the paragraph above, so the heading says what the code
 * already believes.
 *
 * Both lists draw skeleton rows while the first read is in flight, so an empty
 * one says "not here yet" rather than "you have none", which is the only claim
 * the page can honestly make before the answer arrives.
 */
export function WorkSection({ data }: { data: AccountData }) {
  const { tasks, briefs, loading } = data;
  const glance = stepsForGlance(tasks);

  return (
    <div className="grid gap-[var(--dy-gap)] xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* `self-start`: a grid item stretches to the row's height by default, and
          once one column is ranked-and-capped and the other is not, the two
          are rarely the same length any more. Without it the shorter Surface
          sits inside a box padded out to match the taller one - invisible here,
          but not a fight worth leaving for whatever this page grows next to. */}
      <Section className="self-start">
        <SectionHead
          title="Tasks at a glance"
          meta={tasks.length ? `${tasks.length}` : undefined}
          actions={
            <Link
              to="/tasks"
              className="text-fg-3 hover:text-foreground text-xs transition-colors duration-100"
            >
              Every task →
            </Link>
          }
        />
        <Surface>
          {glance.length ? (
            <ul className="divide-line divide-y">
              {glance.map((task) => (
                <li key={task.id} className="flex items-center gap-2.5 px-gutter py-row">
                  <TaskIcon status={task.status} />
                  <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
                  <Link
                    to="/tasks/$taskId"
                    params={{ taskId: task.id }}
                    className="hover:text-gate min-w-0 flex-1 truncate text-base transition-colors duration-100"
                  >
                    {task.title}
                  </Link>
                  {/* The only badges a person would act on. Paused outranks a
                      hold in the ranking above, so a task never shows both. */}
                  {task.pausedAt ? (
                    <PausedChip />
                  ) : holdNeedsAPerson(task) ? (
                    // `heldNote` is only ever null with no `holdReason`, and
                    // `holdNeedsAPerson` already requires one - the `?? undefined`
                    // is for the type, not a case that happens.
                    <Badge variant="gate" title={heldNote(task) ?? undefined}>
                      Held
                    </Badge>
                  ) : null}
                  <span className="w-12 shrink-0 text-right whitespace-nowrap">
                    <Age minutes={minutesSince(task.updatedAt)} hot={task.status === 'review'} />
                  </span>
                </li>
              ))}
            </ul>
          ) : loading ? (
            <RowSkeleton />
          ) : (
            <Empty>Nothing is running on your machines.</Empty>
          )}
        </Surface>
      </Section>

      <Section className="self-start">
        <SectionHead
          title="Your jobs"
          meta={briefs.length ? `${briefs.length}` : undefined}
          actions={
            <Link
              to="/jobs"
              className="text-fg-3 hover:text-foreground text-xs transition-colors duration-100"
            >
              All jobs →
            </Link>
          }
        />
        <Surface>
          {briefs.length ? (
            <ul className="divide-line divide-y">
              {briefs.slice(0, 20).map((brief) => (
                <li key={brief.id} className="flex items-start gap-2.5 px-gutter py-row">
                  <BriefIcon status={brief.status} className="mt-[3px]" />
                  <Link
                    to="/jobs/$briefId"
                    params={{ briefId: brief.id }}
                    className="hover:text-gate min-w-0 flex-1 truncate text-base transition-colors duration-100"
                  >
                    {brief.title}
                  </Link>
                  <span className="text-fg-3 hidden w-28 shrink-0 truncate text-xs sm:block">
                    {labelForKind(brief.status)}
                  </span>
                  <span className="mt-[3px] w-12 shrink-0 text-right whitespace-nowrap">
                    <Age
                      minutes={minutesSince(brief.updatedAt)}
                      hot={brief.status === 'awaiting_approval'}
                    />
                  </span>
                </li>
              ))}
            </ul>
          ) : loading ? (
            <RowSkeleton />
          ) : (
            <Empty>You have not opened a job yet.</Empty>
          )}
        </Surface>
      </Section>
    </div>
  );
}
